import React, { createContext, useContext, useState } from 'react';
import { auth, firestore } from '../lib/firebase';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { collection, query, where, getDocs, setDoc, doc, getDoc, deleteDoc, writeBatch , serverTimestamp } from 'firebase/firestore';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail as firebaseSendPasswordResetEmail } from 'firebase/auth';
import { setCookie, removeCookie, getCookie } from '../utils/cookieUtil';
import { v4 as uuidv4 } from 'uuid';



export const checkEmailExist = async (email) => {
  try {
    const signInMethods = await fetchSignInMethodsForEmail(auth, email);
    return signInMethods;
  } catch (error) {
    console.error('Error fetching sign-in methods:', error);
    throw error;
  }
};

export const isAdminUser = async (email) => {
  try {
    const adminUsersRef = collection(firestore, 'AdminUsers');
    const q = query(adminUsersRef, where('email', '==', email));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking admin user:', error);
    throw new Error('Error checking admin user');
  }
};

export const getAdminUserDetails = async (email) => {
  try {
    const adminUsersRef = collection(firestore, 'AdminUsers');
    const q = query(adminUsersRef, where('email', '==', email));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error('Admin user not found');
    }

    const userDoc = snapshot.docs[0].data();
    return {
      firstName: userDoc.firstName,
      lastName: userDoc.lastName,
      roleId: userDoc.roleId
    };
  } catch (error) {
    console.error('Error getting admin user details:', error);
    throw new Error('Error getting admin user details');
  }
};


const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(getCookie('user') || null);

  const logoutUser = async () => {
    try {
      console.log("Starting logout process...");

      removeCookie('user');
      setUser(null);
      console.log("User cookie removed.");

      await auth.signOut();
      console.log("User signed out successfully.");

      window.location.reload();
    } catch (error) {
      console.error('Error logging out:', error);
      throw new Error('Logout failed');
    }
  };

  return (
    <UserContext.Provider value={{ user, setUser, logoutUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);


export const loginUser = async (email, password, navigate) => {
  try {
    const isAdmin = await isAdminUser(email);
    if (isAdmin) {   
      await signInWithEmailAndPassword(auth, email, password);
      
      const userDetails = await getAdminUserDetails(email);
      if (userDetails) {
        
        setCookie('user', userDetails);
        const roleId = userDetails.roleId;
        try {
          const rolesRef = collection(firestore, 'roles');
          const q = query(rolesRef, where('roleId', '==', roleId));
          const snapshot = await getDocs(q);

          if (snapshot.empty) {
            throw new Error('Admin user not found');
          }

          const userDoc = snapshot.docs[0].data();
          setCookie('roleId', userDoc.permissionList);

        } catch (error) {
          console.error('Error getting admin user details:', error);
          throw new Error('Error getting admin user details');
        }

        navigate('/app');
        window.location.reload()
      }

    } else {
      throw new Error('User is not an admin');
    }
  } catch (error) {
    console.error('Error logging in:', error);
    throw new Error('Login failed');
  }
};

export const createAdminUser = async (email, password, firstName, lastName) => {
  try {

    var appgroup = null;

    const userExistsInAuth = await checkEmailExist(email);

    if (userExistsInAuth.length === 0) {

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const UsersListRef = collection(firestore, 'UsersList');
      const emailQuery = query(UsersListRef, where('email', '==', email));
      const querySnapshot = await getDocs(emailQuery);

      if (querySnapshot.empty) {

        await setDoc(doc(firestore, 'AdminUsers', user.uid), {
          userId: user.uid,
          email,
          firstName,
          lastName,
          password,
          role: "Admin",
          roleId: "1AZhOiCk2sQ8cZcWy0iu"
        });

        return { success: true, userId: user.uid, newUser: true };

      } else {

        querySnapshot.forEach((doc) => {
          appgroup = doc.data().group;

        });



        await setDoc(doc(firestore, 'AdminUsers', user.uid), {
          userId: user.uid,
          email,
          firstName,
          lastName,
          password,
          role: "Admin",
          roleId: "1AZhOiCk2sQ8cZcWy0iu"
        });

        await setDoc(doc(firestore, 'Users', user.uid), {
          email: email,
          group: appgroup,
          name: {
            firstName: firstName,
            secondName: lastName
          },
          temp_password_changed: true,
          uid: user.uid
        });

        return { success: true, userId: user.uid, newUser: true };
      }

    } else {

      const adminUsersRef = collection(firestore, 'AdminUsers');
      const emailQuery = query(adminUsersRef, where('email', '==', email));
      const querySnapshot = await getDocs(emailQuery);

      if (querySnapshot.empty) {

        const randomId = uuidv4();

        await setDoc(doc(firestore, 'AdminUsers', randomId), {
          userId: randomId,
          email,
          firstName,
          lastName,
          role: "Admin",
          roleId: "1AZhOiCk2sQ8cZcWy0iu"
        });

        return { success: true, message: 'User data updated with random ID.', newUser: false };
      } else {
        return { success: false, message: 'User already exists.' };
      }

    }

  } catch (error) {
    return {
      success: false,
      message: error?.message?.includes("auth/quota-exceeded")
        ? "Today's quota for user upload has been reached."
        : error?.message || "An unknown error occurred.",
    };
  }
};

export const createFaculty = async (email, password, firstName, lastName) => {
  try {
    var appgroup = null;

    const userExistsInAuth = await checkEmailExist(email);

    if (userExistsInAuth.length === 0) {

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;


      const UsersListRef = collection(firestore, 'UsersList');
      const emailQuery = query(UsersListRef, where('email', '==', email));
      const querySnapshot = await getDocs(emailQuery);



      if (querySnapshot.empty) {

        await setDoc(doc(firestore, 'AdminUsers', user.uid), {
          userId: user.uid,
          email,
          firstName,
          lastName,
          password,
          role: "faculty",
          roleId: "ZZQVXbqjZJVd6V9BNcjq"
        });

        return { success: true, userId: user.uid, newUser: true };

      } else {
        querySnapshot.forEach((doc) => {
          appgroup = doc.data().group;
        });

        await setDoc(doc(firestore, 'AdminUsers', user.uid), {
          userId: user.uid,
          email,
          firstName,
          lastName,
          password,
          role: "faculty",
          roleId: "ZZQVXbqjZJVd6V9BNcjq"
        });

        await setDoc(doc(firestore, 'Users', user.uid), {
          email: email,
          group: appgroup,
          name: {
            firstName: firstName,
            secondName: lastName
          },
          temp_password_changed: true,
          userId: user.uid
        });

        return { success: true, userId: user.uid, newUser: true };
      }
    } else {

      const adminUsersRef = collection(firestore, 'AdminUsers');
      const emailQuery = query(adminUsersRef, where('email', '==', email));
      const querySnapshot = await getDocs(emailQuery);

      if (querySnapshot.empty) {

        const randomId = uuidv4();

        await setDoc(doc(firestore, 'AdminUsers', randomId), {
          userId: randomId,
          email,
          firstName,
          lastName,
          role: "faculty",
          roleId: "ZZQVXbqjZJVd6V9BNcjq"
        });

        return { success: true, message: 'User data updated with random ID.', newUser: false };
      } else {
        return { success: false, message: 'User already exists.' };
      }

    }

  } catch (error) {
    return {
      success: false,
      message: error?.message?.includes("auth/quota-exceeded")
        ? "Today's quota for user upload has been reached."
        : error?.message || "An unknown error occurred.",
    };
  }
};

export const createAppUser = async (email, firstName, secondName, group) => {
  var adminUid = null;
  try {
    console.log("checking if mail exist in auth table");
    const userExistsInAuth = await checkEmailExist(email);
    setTimeout(150)
    console.log("completed checking if mail exist in auth table");
    if (userExistsInAuth.length === 0) {
      console.log("user not exist in auth");
      const UsersListRef = collection(firestore, 'UsersList');
      const emailQuery = query(UsersListRef, where('email', '==', email));
      const querySnapshot = await getDocs(emailQuery);

      const randomId = uuidv4();

      if (querySnapshot.empty) {
        console.log("user not exist in firestore");

        const rtnVal = await setDoc(doc(firestore, 'UsersList', randomId), {
          email,
          firstName,
          secondName,
          group
        });
        console.log("setDoc result = ", rtnVal);

        return { success: true, message: 'User created successfully.', newUser: true };
      } else {
        return { success: false, message: 'User already exists.' };
      }

    } else {
      const AdminuidRef = collection(firestore, 'AdminUsers');
      const emailQueryuid = query(AdminuidRef, where('email', '==', email));
      const querySnapshotuid = await getDocs(emailQueryuid);

      if (querySnapshotuid.empty) {
        return { success: false, message: 'This user is already signed in' };
      }

      querySnapshotuid.forEach((doc) => {
        adminUid = doc.id
        // setAdminuid(doc.id)
      });



      const UsersListRef = collection(firestore, 'UsersList');
      const emailQuery = query(UsersListRef, where('email', '==', email));
      const querySnapshot = await getDocs(emailQuery);


      if (querySnapshot.empty) {
        await setDoc(doc(firestore, 'UsersList', adminUid), {
          email: email,
          group: group,
          firstName: firstName,
          secondName: secondName
        });

        await setDoc(doc(firestore, 'Users', adminUid), {
          email: email,
          group: group,
          name: {
            firstName: firstName,
            secondName: secondName
          },
          temp_password_changed: true,
          uid: adminUid
        });
        return { success: true, message: 'User created successfully.', newUser: false };
      } else {
        return { success: false, message: 'User already exists.' };
      }

    }

  } catch (error) {
    console.log(error);
    return {
      success: false,
      message: error?.message?.includes("auth/quota-exceeded")
        ? "Today's quota for user upload has been reached."
        : error?.message || "An unknown error occurred.",
    };


  }

};

export const sendPasswordResetEmail = async (email) => {
  try {
    await firebaseSendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    return { success: false, error: error.message };
  }
};

export async function getRegisteredUsers() {
  try {
    const snapshot = await getDocs(collection(firestore, 'Users'));
    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));



    return new Response(JSON.stringify({ users }), {
      status: 200,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.log(error);

    return new Response(JSON.stringify({ error: 'Failed to fetch users' }), {
      status: 500,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
}

export async function getInvitedUsers() {
  try {
    const snapshot = await getDocs(collection(firestore, 'UsersList'));
    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    return new Response(JSON.stringify({ users }), {
      status: 200,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.log(error);

    return new Response(JSON.stringify({ error: 'Failed to fetch users' }), {
      status: 500,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
}

export async function getAdminUsers() {
  try {
    const q = query(collection(firestore, 'AdminUsers'), where('role', '==', 'Admin'));
    const snapshot = await getDocs(q);

    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    return new Response(JSON.stringify({ users }), {
      status: 200,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error('[getFaculty] Error fetching Admin users:', error);

    return new Response(JSON.stringify({ error: 'Failed to fetch users' }), {
      status: 500,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
}

export async function getFacultyUsers() {
  try {
    const q = query(collection(firestore, 'AdminUsers'), where('role', '==', 'faculty'));
    const snapshot = await getDocs(q);

    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    return new Response(JSON.stringify({ users }), {
      status: 200,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error('[getFaculty] Error fetching Admin users:', error);

    return new Response(JSON.stringify({ error: 'Failed to fetch users' }), {
      status: 500,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
}

export const confirmDeleteRegisteredUser = async (userId) => {
  try {
    const userToDelete = userId.toString();
    if (!userToDelete || typeof userToDelete !== "string") {
      throw new Error("User ID must be a non-empty string.");
    }

    const collectionsToDelete = ["Users", "Goals", "Fresh Start", "Cumulative Balance"];
    const archiveRef = doc(firestore, "Archive", userToDelete);
    const archiveData = {}; // Collect everything here

    // --- Handle single-document collections ---
    await Promise.all(
      collectionsToDelete.map(async (collectionName) => {
        try {
          const docRef = doc(firestore, collectionName, userToDelete);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            archiveData[collectionName] = docSnap.data();
            await deleteDoc(docRef);
            console.log(`Archived and deleted from '${collectionName}' for user: ${userToDelete}`);
          } else {
            console.log(`[INFO] No document found in '${collectionName}' for user: ${userToDelete}`);
          }
        } catch (err) {
          console.error(`[ERROR] '${collectionName}' deletion failed:`, err);
        }
      })
    );

    // --- Handle multi-document collections ---
    const archiveCollectionDocs = async (collectionName) => {
      const colRef = collection(firestore, collectionName);
      const q = query(colRef, where("userId", "==", userToDelete));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        archiveData[collectionName] = snapshot.docs.map((doc) => doc.data());

        const batch = writeBatch(firestore);
        snapshot.forEach((doc) => batch.delete(doc.ref));
        await batch.commit();
        console.log(`[SUCCESS] Archived and deleted ${snapshot.size} from '${collectionName}'`);
      } else {
        console.log(`[INFO] No documents found in '${collectionName}' for user: ${userToDelete}`);
      }
    };

    await archiveCollectionDocs("Review");
    await archiveCollectionDocs("Daily Steps");

    // --- Add single deletedAt timestamp and archive everything at once ---
    archiveData["DeletedAt"] = serverTimestamp();
    await setDoc(archiveRef, archiveData, { merge: true });

  } catch (error) {
    console.error("[ERROR] Firestore deletion failed:", {
      errorCode: error.code,
      errorMessage: error.message,
      stack: error.stack,
    });
    throw error;
  }
};


// export const confirmDeleteRegisteredUser = async (userId) => {
//   try {
//     console.log("userId:", userId);
//     console.log("typeof userId:", typeof userId);
//     const userToDelete = userId.toString();
//     if (!userToDelete || typeof userToDelete !== "string") {
//       throw new Error("User ID must be a non-empty string.");
//     }

//     // --- Check & delete from specific collections by doc ID ---
//     const collectionsToDelete = ["Users", "Goals", "Fresh Start", "Cumulative Balance"];

//     await Promise.all(
//       collectionsToDelete.map(async (collectionName) => {
//         try {
//           const docRef = doc(firestore, collectionName, userToDelete);
//           await deleteDoc(docRef)
//           .then(() => {
//             console.log(`Document of '${userToDelete}' has been deleted successfully.`);
//           })
//           .catch(error => {
//             console.log(error);
//           });
//         } catch (innerErr) {
//           console.error(`[ERROR] Failed on '${collectionName}' deletion:`, innerErr);
//         }
//       })
//     );

//     // --- Delete documents from the Review collection ---
//     const reviewsCollectionRef = collection(firestore, "Review");
//     const reviewsQuery = query(reviewsCollectionRef, where("userId", "==", userToDelete));

//     const reviewsQuerySnapshot = await getDocs(reviewsQuery);

//     if (!reviewsQuerySnapshot.empty) {
//       const batch = writeBatch(firestore);
//       reviewsQuerySnapshot.forEach((doc) => {
//         batch.delete(doc.ref);
//       });
//       await batch.commit();
//       console.log(`[SUCCESS] Deleted ${reviewsQuerySnapshot.size} reviews for user: ${userToDelete}`);
//     } else {
//       console.log(`[INFO] No reviews found for user: ${userToDelete}`);
//     }

//     // --- Delete documents from the Daily Steps collection ---
//     const dailyStepsCollectionRef = collection(firestore, "Daily Steps");
//     const dailyStepsQuery = query(dailyStepsCollectionRef, where("userId", "==", userToDelete));

//     const dailyStepsQuerySnapshot = await getDocs(dailyStepsQuery);

//     if (!dailyStepsQuerySnapshot.empty) {
//       const batch = writeBatch(firestore);
//       dailyStepsQuerySnapshot.forEach((doc) => {
//         batch.delete(doc.ref);
//       });
//       await batch.commit();
//       console.log(`[SUCCESS] Deleted ${dailyStepsQuerySnapshot.size} daily steps for user: ${userToDelete}`);
//     } else {
//       console.log(`[INFO] No daily steps found for user: ${userToDelete}`);
//     }

//   } catch (error) {
//     console.error('[ERROR] Firestore deletion failed:', {
//       errorCode: error.code,
//       errorMessage: error.message,
//       stack: error.stack
//     });
//     throw error;
//   }
// };

export const confirmDeleteInvitedUser = async (userEmail) => {
  try {

    if (!userEmail) {
      throw new Error('Email is missing for deletion');
    }

    const UsersListCollectionRef = collection(firestore, "UsersList");
    const UsersListQuery = query(UsersListCollectionRef, where("email", "==", userEmail));

    const UsersListQuerySnapshot = await getDocs(UsersListQuery);

    if (!UsersListQuerySnapshot.empty) {
      const batch = writeBatch(firestore);
      UsersListQuerySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      console.log(`[SUCCESS] Deleted user: ${userEmail}`);
    } else {
      console.log(`[INFO] No user found : ${userEmail}`);
    }

  } catch (error) {
    console.error('[ERROR] Firestore deletion failed:', {
      errorCode: error.code,
      errorMessage: error.message,
      stack: error.stack
    });
    throw error;
  }
};

export const confirmDeleteFacultyUser = async (userToDelete) => {
  try {
    if (!userToDelete) {
      throw new Error('No user specified for deletion');
    }
    const deleteFaculty = deleteDoc(doc(firestore, "AdminUsers", userToDelete));

  } catch (error) {
    console.error('[ERROR] Firestore deletion failed:', {
      errorCode: error.code,
      errorMessage: error.message,
      stack: error.stack
    });
    throw error;
  }
};

export const confirmDeleteAdminUser = async (userToDelete) => {
  try {
    if (!userToDelete) {
      throw new Error('No user specified for deletion');
    }
    const deleteFaculty = deleteDoc(doc(firestore, "AdminUsers", userToDelete));

  } catch (error) {
    console.error('[ERROR] Firestore deletion failed:', {
      errorCode: error.code,
      errorMessage: error.message,
      stack: error.stack
    });
    throw error;
  }
};
